/**
 * Login Page - Epicor Authentication
 * Distinctive split-panel with animated Trendy mascot and brand personality
 */

import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Eye, EyeOff, Loader2, AlertCircle, ArrowRight } from 'lucide-react'

export default function Login() {
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    clearError()

    try {
      await login(username, password)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Panel — Brand showcase */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Deep gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#003d6b] via-amtrend-blue to-[#0088f0]" />

        {/* Subtle mesh overlay for depth */}
        <div className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 20%, white 0%, transparent 50%),
                              radial-gradient(circle at 70% 80%, white 0%, transparent 50%)`,
          }}
        />

        {/* Geometric accent shapes */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-white/5 -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-white/5 translate-y-1/3 -translate-x-1/3" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/10" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-14">
          {/* Top — Logo */}
          <div className="flex items-center gap-3">
            <img
              src="/amtrend-logo.png"
              alt="Amtrend"
              className="h-10 brightness-0 invert"
            />
          </div>

          {/* Center — Trendy hero */}
          <div className="flex flex-col items-center justify-center flex-1 py-8">
            <div className="relative">
              {/* Glow effect behind penguin */}
              <div className="absolute inset-0 m-auto w-48 h-48 bg-white/10 rounded-full blur-3xl" />
              <img
                src="/Trendy the Penguin.png"
                alt="Trendy"
                className="relative h-52 xl:h-64 object-contain drop-shadow-2xl animate-fade-in-up"
              />
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold text-white tracking-tight mt-6 animate-fade-in-up"
              style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
              Trendy
            </h1>
            <p className="text-blue-200/80 text-lg mt-2 font-light animate-fade-in-up"
              style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
              Your AI-powered ERP companion
            </p>
          </div>

          {/* Bottom — Feature pills */}
          <div className="flex flex-wrap gap-3 animate-fade-in-up"
            style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
            {[
              { dot: 'bg-emerald-400', label: 'Real-time Epicor data' },
              { dot: 'bg-emerald-400', label: 'Natural language queries' },
              { dot: 'bg-emerald-400', label: 'M365 integrated' },
            ].map(({ dot, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white/90 border border-white/10"
              >
                <span className={`w-1.5 h-1.5 ${dot} rounded-full`} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile-only logo + mascot */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/amtrend-logo.png"
              alt="Amtrend"
              className="h-10 mx-auto mb-4"
            />
            <img
              src="/Trendy the Penguin.png"
              alt="Trendy"
              className="h-20 mx-auto mb-2"
            />
            <h1 className="font-display text-xl font-bold text-gray-900">Trendy</h1>
            <p className="text-gray-500 text-sm">AI-powered ERP companion</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-elevation-3 border border-gray-100 p-8">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-1">
              Welcome back
            </h2>
            <p className="text-gray-500 text-sm mb-8">
              Sign in with your Epicor credentials
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2.5 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-scale-in">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Username */}
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/20 focus:border-amtrend-blue transition-all"
                  placeholder="Enter your Epicor username"
                  autoComplete="username"
                  autoFocus
                  disabled={submitting}
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/20 focus:border-amtrend-blue transition-all"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={submitting}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-lg"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || !username || !password}
                className="w-full py-3.5 px-4 bg-amtrend-blue text-white font-semibold rounded-xl hover:bg-amtrend-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amtrend-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 group"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-400 mt-6">
            Need help? Contact your system administrator
          </p>
        </div>
      </div>
    </div>
  )
}
